// ConditionValueEditor.jsx

import React from 'react';

// Utilities
import {
  isDateLikeFieldType,
  isRelativeTemporalOperator,
  operatorNeedsUnit,
  operatorNeedsValue,
  VALUELESS_CONDITION_OPERATORS,
  normalizeRelativeUnit,
} from '../../../utils/automationConditionFilters';

// Components
import AutomationLiteralInput from '../../AutomationLiteralInput';
import NumericBetweenEditor from './NumericBetweenEditor';
import RelativeDateValueEditor from './RelativeDateValueEditor';

/**
 * ConditionValueEditor
 *
 * Renders the value editor addon for a condition row based on field type and operator.
 *
 * @param {object} rule - The parent automation rule.
 * @param {object} conditionRow - The current condition row being edited.
 * @param {string} conditionFieldType - Normalized field type for the row field.
 * @param {string} selectedConditionOperator - Active operator for the row.
 * @param {*} literalValue - Current literal value payload for the row.
 * @param {object} theme - Theme object used for dynamic styling.
 * @param {Map} fieldTypeByKey - Field type lookup keyed by field key.
 * @param {Map} fieldEnumOptionsByKey - Enum option lookup keyed by field key.
 * @param {function} updateConditionRows - Updates flattened condition rows for a rule.
 *
 * @returns {ReactNode} The rendered condition value editor.
 */
const ConditionValueEditor = React.memo(({
  rule,
  conditionRow,
  conditionFieldType,
  selectedConditionOperator,
  literalValue,
  theme,
  fieldTypeByKey,
  fieldEnumOptionsByKey,
  updateConditionRows,
}) => {

  const isDateCondition = isDateLikeFieldType(conditionFieldType);
  const isRelativeDateCondition = isRelativeTemporalOperator(conditionFieldType, selectedConditionOperator);
  const requiresRelativeValue = operatorNeedsValue(conditionFieldType, selectedConditionOperator);
  const requiresRelativeUnit = operatorNeedsUnit(conditionFieldType, selectedConditionOperator);
  const hidesLiteralInput = VALUELESS_CONDITION_OPERATORS.has(selectedConditionOperator);
  const isNumericBetween = (
    conditionFieldType === 'number'
    || conditionFieldType === 'money'
    || conditionFieldType === 'percentage'
  ) && selectedConditionOperator === 'between';

  // ----- UTILITIES ----- //

  const updateCurrentRow = (updater) => {
    updateConditionRows(rule.key, (flatRows) => flatRows.map((entry) => {
      if (entry.key !== conditionRow.key) return entry;
      return updater(entry);
    }));
  };
  if (isDateCondition && isRelativeDateCondition) {
    return (
      <RelativeDateValueEditor
        theme={theme}
        literalValue={literalValue}
        requiresRelativeValue={requiresRelativeValue}
        requiresRelativeUnit={requiresRelativeUnit}
        onRelativeUnitChange={(nextUnit) => {
          updateCurrentRow((entry) => ({
            ...entry,
            value: {
              type: 'literal',
              literal: requiresRelativeValue
                ? {
                  value: entry?.value?.literal?.value ?? 1,
                  unit: nextUnit,
                }
                : {
                  value: null,
                  unit: nextUnit,
                },
            },
          }));
        }}
        onRelativeValueChange={(nextValue) => {
          updateCurrentRow((entry) => ({
            ...entry,
            value: {
              type: 'literal',
              literal: {
                value: nextValue,
                unit: normalizeRelativeUnit(entry?.value?.literal?.unit) || 'days',
              },
            },
          }));
        }}
      />
    );
  }

  if (isNumericBetween) {
    return (
      <NumericBetweenEditor
        literalValue={literalValue}
        theme={theme}
        onMaxChange={(nextMax) => {
          updateCurrentRow((entry) => ({
            ...entry,
            value: {
              type: 'literal',
              literal: {
                min: entry?.value?.literal?.min ?? '',
                max: nextMax,
              },
            },
          }));
        }}
        onMinChange={(nextMin) => {
          updateCurrentRow((entry) => ({
            ...entry,
            value: {
              type: 'literal',
              literal: {
                min: nextMin,
                max: entry?.value?.literal?.max ?? '',
              },
            },
          }));
        }}
      />
    );
  }

  if (hidesLiteralInput) {

    return <div className="automation-rule-row-spacer" />;
  }

  // ----- RENDER ----- //

  return (
    <AutomationLiteralInput
      fieldKey={conditionRow?.fieldKey || ''}
      operator={selectedConditionOperator}
      value={literalValue}
      theme={theme}
      fieldTypeByKey={fieldTypeByKey}
      fieldEnumOptionsByKey={fieldEnumOptionsByKey}
      onValueChange={(nextValue) => {
        updateCurrentRow((entry) => ({
          ...entry,
          value: {
            type: 'literal',
            literal: nextValue,
          },
        }));
      }}
    />
  );
});

// Dev Tools
ConditionValueEditor.displayName = 'ConditionValueEditor';

export default ConditionValueEditor;
